/**
 * Storage for method markers — decorators that record *which* methods a cross-cutting
 * concern applies to, and optionally how, without adding behaviour of their own.
 *
 * Each concern exports its own marker with its own name, signature and options type, and
 * this only supplies the shared bookkeeping. Exported because {@link ProxyDecorator} takes
 * one — it is the gate every decorator is built on — so defining a decorator outside this
 * package needs it.
 *
 * Deliberately not `reflect-metadata`: esbuild transforms legacy decorators but ignores
 * `emitDecoratorMetadata`, so a `Reflect.getMetadata` design would pass under ts-jest and
 * then silently find nothing in the bundled Lambda.
 *
 * Each instance owns a private symbol, so concerns never see each other's marks and a
 * method may carry any combination of them in any order.
 *
 * @template TOptions per-method payload; `void` for a marker that is a bare gate
 *
 * @example
 * const marks = new MethodMarks<CacheOptions>('shared-decorators:cachedMethods');
 *
 * export function cached(options?: CacheOptions): MethodDecorator {
 *   return (prototype, propertyKey) => marks.add(prototype, propertyKey, options);
 * }
 *
 * // Note: a delegating function, not `= marks.of` — detaching a method loses `this`.
 * export function cachedMethodsOf(target: object) {
 *   return marks.of(target);
 * }
 */
export class MethodMarks<TOptions = void> {
  private readonly key: symbol;

  /**
   * Shared result for an unmarked target. `of` runs on every property access of a
   * decorated object, so it must not allocate on the miss path.
   */
  private readonly none: ReadonlyMap<string, TOptions | undefined> = new Map();

  constructor(description: string) {
    this.key = Symbol(description);
  }

  /** Records `propertyKey`, with its options if the marker carries any. */
  public add(prototype: object, propertyKey: string | symbol, options?: TOptions): void {
    const host = prototype as Record<symbol, Map<string, TOptions | undefined>>;

    // Copy-on-write per class: a subclass inherits its parent's marks through the
    // prototype chain, but must own its map before adding to it — otherwise it would
    // mark the method on its parent too.
    if (!Object.prototype.hasOwnProperty.call(host, this.key)) {
      host[this.key] = new Map(host[this.key] ?? []);
    }

    host[this.key].set(String(propertyKey), options);
  }

  /**
   * The marks on `target` or anywhere up its prototype chain, keyed by method name.
   * Empty when nothing is marked.
   *
   * Safe to call on a proxied target: {@link ProxyDecorator} returns non-function
   * property reads verbatim, so the lookup reaches the underlying instance.
   */
  public of(target: object): ReadonlyMap<string, TOptions | undefined> {
    const host = target as Record<symbol, Map<string, TOptions | undefined>>;

    return host[this.key] ?? this.none;
  }
}
